import { Component, type ReactNode, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { CardRecord, LocalParagraphScore, ParagraphRecallRecord, SrsFeedback, WritingContent } from '../db/indexedDb';
import { CARD_TYPE_ORDER } from '../srs/dashboard';
import type { PersistedReview } from '../srs/reviewDb';
import SpeakingRelearnCard from './SpeakingRelearnCard';
import SpeakingLightReviewCard from './SpeakingLightReviewCard';
import ParagraphRecallCard from '../writing/paragraphRecall/ParagraphRecallCard';
import TopicRecallCard from '../writing/topicRecall/TopicRecallCard';
import { TopicDictationCard } from '../writing/topicDictation';
import { deriveWritingSrsAdjustments } from '../writing/paragraphRecall/srsAdjustments';
import WritingClozeCard, { type WritingClozeResult } from '../writing/lightReview/WritingClozeCard';
import { ListeningResult } from '../listening/types';
import VocabFlipCard from './VocabFlipCard';
import SpeakingVocabCard from './SpeakingVocabCard';
import { type VocabPracticeCard } from '../services/srsQueue/vocabQueue';
import ListeningCard from './ListeningCard';
import { TranslationPractice } from '../writing/aiWriting/TranslationPractice';
import { ArgumentPractice } from '../writing/aiWriting/ArgumentPractice';
import type { TranslationCard, ArgumentCard } from '../writing/aiWriting/types';
import { useSessionState } from './hooks/useSessionState';
import { useKeyboardShortcuts } from '../hooks/useKeyboardShortcuts';

const assertCardType = (value?: string) =>
  CARD_TYPE_ORDER.find((item) => item === value) ?? undefined;

class CardErrorBoundary extends Component<{ onError: (error: Error) => void; children: ReactNode }, { hasError: boolean }> {
  constructor(props: { onError: (error: Error) => void; children: ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: Error) {
    this.props.onError(error);
  }

  componentDidUpdate(prevProps: Readonly<{ onError: (error: Error) => void; children: ReactNode }>) {
    if (prevProps.children !== this.props.children && this.state.hasError) {
      this.setState({ hasError: false });
    }
  }

  render() {
    if (this.state.hasError) return null;
    return this.props.children;
  }
}

const fallbackCard = (card: CardRecord) => (
  <div className="session-card">
    <p className="eyebrow">通用卡片</p>
    <h3>{card.type}</h3>
    <pre className="session-pre">{JSON.stringify(card.content, null, 2)}</pre>
  </div>
);

function SessionPage() {
  const { type: typeParam } = useParams();
  const isMixed = typeParam === 'mixed';
  const typeFilter = assertCardType(typeParam);

  const { state, actions, controllerRef } = useSessionState(typeFilter, isMixed);
  const {
    loadState, snapshot, saving, error, cardError, renderTick, ended,
    lastWritingScore, lastRecallRecord, lastWritingCloze, lastListeningResult,
    topicDictationResult,
    hasNewAction, hasSpeakingRecording, hasWritingAnswer, aiWritingEvaluated,
  } = state;

  const handleFeedback = async (feedback: SrsFeedback) => {
    if (saving || ended) return;
    const controller = controllerRef.current;
    if (!controller) return;

    actions.setSaving(true);
    actions.setError(undefined);
    actions.setCardError(null);

    try {
      let reviewOptions;

      if (activeCard?.type === 'writing') {
        const writingContent = activeCard.content as WritingContent | undefined;
        const isClozeCard =
          Boolean(activeCard.writingMeta?.cloze) ||
          Boolean(writingContent?.clozeTemplate && writingContent?.clozeAnswers);

        if (isClozeCard && lastWritingCloze) {
          reviewOptions = {
            notes: `writing-cloze:${lastWritingCloze.accuracy}`,
            skillConfig: deriveWritingSrsAdjustments(lastWritingCloze.accuracy),
            answerMeta: { writingClozeResult: lastWritingCloze },
          };
        } else if (activeCard.mode === 'topic-dictation' && topicDictationResult) {
          reviewOptions = {
            notes: `topic-dictation:${topicDictationResult.averageCoverage}`,
            skillConfig: deriveWritingSrsAdjustments(topicDictationResult.averageCoverage),
            answerMeta: { topicDictationResult },
          };
        } else if (lastRecallRecord) {
          reviewOptions = {
            notes: `paragraph-coverage:${lastRecallRecord.localScore.coverage}`,
            skillConfig: deriveWritingSrsAdjustments(lastRecallRecord.localScore.coverage),
            answerMeta: {
              paragraphRecallLocalScore: lastRecallRecord.localScore,
              paragraphRecallLLMFeedback: lastRecallRecord.llmFeedback,
              paragraphRecallAnswer: lastRecallRecord.answer,
              paragraphRecallEvaluatedAt: lastRecallRecord.evaluatedAt,
            },
          };
        } else if (lastWritingScore) {
          reviewOptions = {
            notes: `paragraph-coverage:${lastWritingScore.coverage}`,
            skillConfig: deriveWritingSrsAdjustments(lastWritingScore.coverage),
            answerMeta: { paragraphRecallLocalScore: lastWritingScore },
          };
        }
      }

      if (activeCard?.type === 'listening' && lastListeningResult) {
        reviewOptions = {
          ...(reviewOptions ?? {}),
          notes: reviewOptions?.notes ?? `listening-${lastListeningResult.mode}:${lastListeningResult.accuracy}`,
          answerMeta: { ...(reviewOptions?.answerMeta ?? {}), listeningResult: lastListeningResult },
        };
      }

      const result = await controller.submit(feedback, reviewOptions);

      // 记录学习时间
      if (activeCard) {
        const score = lastWritingScore?.coverage
          ?? lastRecallRecord?.localScore.coverage
          ?? lastWritingCloze?.accuracy
          ?? topicDictationResult?.averageCoverage
          ?? lastListeningResult?.accuracy;
        actions.logStudyTime(activeCard.id, activeCard.type, activeCard.mode, feedback, score);
      }

      actions.setSnapshot(result.snapshot);
      actions.setLastOutcome(result.outcome);
      actions.setRenderTick((tick) => tick + 1);
    } catch (err) {
      const message = err instanceof Error ? err.message : '保存结果失败';
      actions.setError(message);
    } finally {
      actions.setSaving(false);
    }
  };

  const handleSkip = () => {
    const controller = controllerRef.current;
    if (!controller || ended) return;
    const snap = controller.skipCurrent();
    actions.setSnapshot(snap);
    actions.setCardError(null);
    actions.setRenderTick((tick) => tick + 1);
    actions.setLastOutcome(undefined);
  };

  const handleGoBack = () => {
    const controller = controllerRef.current;
    if (!controller || ended) return;
    const snap = controller.goBack();
    actions.setSnapshot(snap);
    actions.setCardError(null);
    actions.setRenderTick((tick) => tick + 1);
    actions.setLastOutcome(undefined);
  };

  const handleAdvanceWithoutUpdate = () => {
    const controller = controllerRef.current;
    if (!controller || ended) return;
    const snap = controller.advanceWithoutUpdate();
    actions.setSnapshot(snap);
    actions.setCardError(null);
    actions.setRenderTick((tick) => tick + 1);
    actions.setLastOutcome(undefined);
  };

  const handleRetry = () => {
    actions.setCardError(null);
    actions.setRenderTick((tick) => tick + 1);
  };

  const handleExit = () => {
    const confirmed = window.confirm('已完成记录会保留，未完成将被放弃。确认退出当前 Session 吗？');
    if (confirmed) {
      actions.setEnded(true);
    }
  };

  // 包装 setter 函数，设置结果时同时标记有新操作
  const handleAiWritingEvaluated = (evaluated: boolean) => {
    actions.setAiWritingEvaluated(evaluated);
    if (evaluated) actions.setHasNewAction(true);
  };
  const handleWritingClozeResult = (result: WritingClozeResult) => {
    actions.setLastWritingCloze(result);
    actions.setHasNewAction(true);
  };
  const handleWritingScore = (score: LocalParagraphScore) => {
    actions.setLastWritingScore(score);
    actions.setHasNewAction(true);
  };
  const handleRecallRecord = (record: ParagraphRecallRecord) => {
    actions.setLastRecallRecord(record);
    actions.setHasNewAction(true);
  };
  const handleListeningResult = (result: ListeningResult) => {
    actions.setLastListeningResult(result);
    actions.setHasNewAction(true);
  };
  const handleSpeakingRecorded = () => {
    actions.setHasSpeakingRecording(true);
    actions.setHasNewAction(true);
  };
  const handleWritingAnswerChange = (hasAnswer: boolean) => {
    actions.setHasWritingAnswer(hasAnswer);
    if (hasAnswer) actions.setHasNewAction(true);
  };

  const activeCard = snapshot?.currentCard;
  const activePracticeKey = activeCard
    ? (activeCard as VocabPracticeCard).practiceId ?? activeCard.id
    : undefined;
  const total = snapshot?.total ?? 0;
  const completed = snapshot?.completed ?? 0;
  const skipped = snapshot?.skipped ?? 0;
  const currentIndex = snapshot?.currentIndex ?? 0;
  const isFinished = Boolean(snapshot?.finished);
  const isListeningCardWithoutResult = activeCard?.type === 'listening' && !lastListeningResult;
  const isSpeakingCardWithoutRecording = activeCard?.type === 'speaking' && activeCard?.mode === 'relearn' && !hasSpeakingRecording;
  const isAiWritingCard = activeCard?.type === 'writing' && (activeCard?.mode === 'translation' || activeCard?.mode === 'argument');
  const isAiWritingCardWithoutEval = isAiWritingCard && !aiWritingEvaluated;
  const isTopicDictationWithoutResult = activeCard?.type === 'writing' && activeCard?.mode === 'topic-dictation' && !topicDictationResult;
  const isWritingCardWithoutAnswer = activeCard?.type === 'writing' && !isAiWritingCard && activeCard?.mode !== 'topic-recall' && activeCard?.mode !== 'topic-dictation' && !activeCard?.writingMeta?.cloze && !(activeCard?.content as WritingContent | undefined)?.clozeTemplate && !hasWritingAnswer;
  const isCurrentSubmitted = controllerRef.current?.isCurrentSubmitted() ?? false;
  const canGoBack = currentIndex > 0 && !isFinished;

  const loadMessage = useMemo(() => {
    if (loadState.status === 'loading') return '正在加载今日任务……';
    if (loadState.status === 'error') return loadState.message;
    if (ended) return '已退出本次练习';
    if (isFinished && total === 0) return '本次队列为空';
    if (total === 0) return '今日暂无可练习的卡片，先回到看板安排任务吧。';
    return '';
  }, [ended, isFinished, loadState, total]);

  const pageTitle = typeFilter ? `练习 · ${typeFilter}` : isMixed ? '混合练习流' : '通用练习流';

  const renderActiveCard = (card: CardRecord) => {
    switch (card.type) {
      case 'speaking':
        return card.mode === 'review' ? <SpeakingLightReviewCard card={card} /> : <SpeakingRelearnCard card={card} onRecorded={handleSpeakingRecorded} />;
      case 'writing': {
        const writingContent = card.content as WritingContent | undefined;
        if (card.mode === 'translation') {
          const aiCard = {
            id: card.id,
            type: 'translation' as const,
            topic: card.writingMeta?.topicTag ?? '',
            topicCn: '',
            chinese: writingContent?.prompt ?? '',
            english: writingContent?.answer ?? '',
            keywords: (card.writingMeta?.keyPoints ?? []) as string[],
          } satisfies TranslationCard;
          return <TranslationPractice card={aiCard} onEvaluated={handleAiWritingEvaluated} />;
        }
        if (card.mode === 'argument') {
          const aiCard = {
            id: card.id,
            type: 'argument' as const,
            topic: card.writingMeta?.topicTag ?? '',
            topicCn: '',
            argument: writingContent?.prompt ?? '',
            keyPoints: (card.writingMeta?.keyPoints ?? []) as string[],
            reference: writingContent?.answer ?? '',
          } satisfies ArgumentCard;
          return <ArgumentPractice card={aiCard} onEvaluated={handleAiWritingEvaluated} />;
        }
        if (card.mode === 'topic-recall') {
          return <TopicRecallCard card={card} onEvaluated={actions.setTopicRecallResult} />;
        }
        if (card.mode === 'topic-dictation') {
          return <TopicDictationCard card={card} onEvaluated={actions.setTopicDictationResult} />;
        }
        if (card.writingMeta?.cloze || writingContent?.clozeTemplate) {
          return <WritingClozeCard card={card} onEvaluated={handleWritingClozeResult} />;
        }
        return (
          <ParagraphRecallCard
            card={card}
            onLocalScore={handleWritingScore}
            onRecallRecord={handleRecallRecord}
            onAnswerChange={handleWritingAnswerChange}
          />
        );
      }
      case 'listening':
        return <ListeningCard card={card} onResult={handleListeningResult} />;
      case 'vocab': {
        const practice = card as VocabPracticeCard;
        const mode = practice.practiceMode ?? 'input';
        if (mode === 'speaking') {
          return <SpeakingVocabCard card={card} />;
        }
        return <VocabFlipCard card={card} />;
      }
      default:
        return fallbackCard(card);
    }
  };

  const feedbackDisabled = !activeCard || saving || !!cardError || isListeningCardWithoutResult || isSpeakingCardWithoutRecording || isWritingCardWithoutAnswer || isAiWritingCardWithoutEval || isTopicDictationWithoutResult;

  // 键盘快捷键
  useKeyboardShortcuts({
    shortcuts: {
      '1': () => !feedbackDisabled && handleFeedback('again'),
      '2': () => !feedbackDisabled && handleFeedback('hard'),
      '3': () => !feedbackDisabled && handleFeedback('good'),
      'arrowleft': () => canGoBack && !saving && handleGoBack(),
      'arrowright': () => isCurrentSubmitted && !hasNewAction && !saving && handleAdvanceWithoutUpdate(),
    },
    enabled: !isFinished && !!activeCard,
  });

  return (
    <div className="session-layout">
      {/* Session Header */}
      <header className="session-header">
        <Link to="/today" className="session-back">←</Link>
        <span className="session-title">{pageTitle.toUpperCase()}</span>
        <span className="session-count">{Math.min(currentIndex + 1, total)}/{total}</span>
      </header>

      {/* Progress Bar */}
      {!isFinished && total > 0 && (
        <div className="session-progress-bar">
          <div className="progress-track">
            <div className="progress-fill" style={{ width: total ? `${(completed / total) * 100}%` : '0%' }} />
            {skipped > 0 && <span className="pill">SKIP {skipped}</span>}
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="session-content">
        {loadMessage && (
          <div className="empty-state">
            <div className="empty-state-icon">📋</div>
            <div className="empty-state-title">{loadMessage}</div>
          </div>
        )}

        {error && (
          <div className="card" style={{ background: 'var(--accent-red)' }}>
            <div className="card-body">{error}</div>
          </div>
        )}

        {isFinished && total > 0 && (
          <div className="session-complete">
            <div className="session-complete-icon">✓</div>
            <h2 className="session-complete-title">
              {ended ? 'SESSION ENDED' : 'SESSION COMPLETE'}
            </h2>
            <div className="session-complete-stats">
              <div className="session-complete-stat">{completed}/{total}</div>
              <div className="session-complete-label">CARDS REVIEWED</div>
            </div>
            <Link className="btn btn-primary" to="/today">BACK TO DASHBOARD →</Link>
          </div>
        )}

        {!isFinished && activeCard && (
          <>
            {cardError ? (
              <div className="card">
                <div className="card-body" style={{ background: 'var(--accent-red)' }}>
                  <p className="font-bold mb-sm">LOAD ERROR</p>
                  <p>{cardError}</p>
                  <div className="flex gap-sm mt-md">
                    <button className="btn" type="button" onClick={handleRetry}>RETRY</button>
                    <button className="btn" type="button" onClick={handleSkip}>SKIP</button>
                  </div>
                </div>
              </div>
            ) : (
              <CardErrorBoundary
                key={`${activePracticeKey ?? activeCard.id}-${renderTick}`}
                onError={(err) => actions.setCardError(err.message || '卡片渲染失败')}
              >
                {renderActiveCard(activeCard)}
              </CardErrorBoundary>
            )}
          </>
        )}
      </div>

      {/* Feedback Buttons */}
      {!isFinished && activeCard && (
        <div className="session-footer">
          <div className="session-nav-row">
            <button
              className="session-nav-btn"
              onClick={handleGoBack}
              disabled={!canGoBack || saving}
              title="上一张"
            >
              ← 上一张
            </button>
            {isCurrentSubmitted && !hasNewAction && (
              <button
                className="session-nav-btn"
                onClick={handleAdvanceWithoutUpdate}
                disabled={saving}
                title="跳过到下一张（不更新数据）"
              >
                下一张 →
              </button>
            )}
          </div>
          <div className="feedback-group">
            <button className="feedback-btn again" onClick={() => handleFeedback('again')} disabled={feedbackDisabled}>
              <span>AGAIN</span>
              <span className="interval">&lt;1d</span>
            </button>
            <button className="feedback-btn hard" onClick={() => handleFeedback('hard')} disabled={feedbackDisabled}>
              <span>HARD</span>
              <span className="interval">1d</span>
            </button>
            <button className="feedback-btn good" onClick={() => handleFeedback('good')} disabled={feedbackDisabled}>
              <span>GOOD</span>
              <span className="interval">3d</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default SessionPage;
